import { useEffect } from "react";
import { SITE } from "@/lib/site";

const BASE = "https://www.beaconcounselingct.com";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SEO({
  title,
  description,
  path = "/",
  image = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200",
  type = "website",
  jsonLd = [],
}) {
  const fullTitle = title
    ? `${title} | ${SITE.name}`
    : `${SITE.name} — Evidence-Based CBT Therapy in Connecticut`;
  const url = `${BASE}${path}`;
  const ldString = JSON.stringify(jsonLd);

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", "index,follow");
    upsertMeta("name", "theme-color", "#0b1b33");
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:site_name", SITE.name);

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // JSON-LD
    const scripts = [];
    const graphs = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    graphs.forEach((data, i) => {
      if (!data) return;
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", `ld-${i}`);
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      scripts.push(s);
    });
    return () => scripts.forEach((s) => s.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, description, url, image, type, ldString]);

  return null;
}

export const orgSchema = {
  "@context": "https://schema.org",
  "@type": ["MedicalBusiness", "LocalBusiness"],
  name: SITE.legal,
  description:
    "Evidence-Based Cognitive Behavioral Therapy (CBT) practice in Connecticut treating anxiety, depression, OCD, ADHD, trauma, and more.",
  url: BASE,
  telephone: SITE.phone,
  email: SITE.email,
  medicalSpecialty: "Psychiatric",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "209 Main Street",
    addressLocality: "Southington",
    addressRegion: "CT",
    postalCode: "06489",
    addressCountry: "US",
  },
  areaServed: "Connecticut",
  image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200",
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${BASE}${it.path}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
